import React from 'react';
import type { Row, ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import UserAvatar from '@/app/refresh-components/UserAvatar';
import type { SocialAccountData } from '../social-accounts-table/social-accounts-table-types';

/**
 * Shared row renderer for social accounts tables.
 * @param rowType - The type of row/column being rendered
 * @param row - The row object from react-table
 * @param col - The column definition
 * @param underlineSocialAccount - If true, underline the socialAccount cell
 */
export function sharedRenderRowSocialAccounts(
  rowType: string,
  row: Row<SocialAccountData>,
  col: ColumnDef<SocialAccountData>,
  underlineSocialAccount: boolean = false,
) {
  switch (rowType) {
    case 'socialAccount':
      return (
        <span className={underlineSocialAccount ? 'text-gray-600 underline underline-offset-2' : 'text-gray-600'}>
          {row.original.socialAccount}
        </span>
      );
    case 'memberName': {
      const memberIds = row.original.memberIds;
      const primaryMemberId = memberIds && memberIds.length > 0 ? memberIds[0] : undefined;

      // If no member IDs, show member name without link
      if (!primaryMemberId) {
        return (
          <div className="flex align-center items-center gap-2">
            <UserAvatar name={row.original.memberName} imageUrl={row.original.memberAvatar} />
            <span className="text-gray-600">{row.original.memberName}</span>
          </div>
        );
      }

      return (
        <Link
          to={`/members/${primaryMemberId}`}
          className={`flex align-center items-center gap-2 ${underlineSocialAccount ? 'text-primary hover:text-primary underline underline-offset-2' : 'text-primary hover:text-primary hover:underline'}`}
          data-dd-action-name="social-ads-social-accounts-table-social-account-link"
          aria-label={`View profile for ${row.original.socialAccount}${memberIds && memberIds.length > 1 ? ` (${memberIds.length} members)` : ''}`}
        >
          <UserAvatar name={row.original.memberName} imageUrl={row.original.memberAvatar} />
          <span className="text-gray-600">
            {row.original.memberName}
            {memberIds && memberIds.length > 1 && (
              <span className="ml-1 text-xs text-gray-400">+ {memberIds.length}</span>
            )}
          </span>
        </Link>
      );
    }
    case 'roas':
      return <span className="text-gray-600">{row.original.roas}</span>;
    case 'impressions':
      return <span className="text-gray-600">{row.original.impressions.toLocaleString()}</span>;
    case 'reach':
      return <span className="text-gray-600">{row.original.reach.toLocaleString()}</span>;
    case 'clicks':
      return <span className="text-gray-600">{row.original.clicks.toLocaleString()}</span>;
    case 'ctr':
      return (
        <span className="text-gray-600">{row.original.ctr ? `${(row.original.ctr * 100).toFixed(2)}%` : 'N/A'}</span>
      );
    case 'cpm':
      return <span className="text-gray-600">{row.original.cpm ? `$${row.original.cpm.toFixed(2)}` : 'N/A'}</span>;
    case 'cpc':
      return <span className="text-gray-600">{row.original.cpc ? `$${row.original.cpc.toFixed(2)}` : 'N/A'}</span>;
    case 'spend':
      return (
        <span className="text-gray-600">
          ${row.original.spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      );
    case 'sales':
      return (
        <span className="text-gray-600">
          ${row.original.sales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      );
    default:
      return (
        <span>
          {row.getValue((col as ColumnDef<SocialAccountData> & { accessorKey: keyof SocialAccountData }).accessorKey)}
        </span>
      );
  }
}
